/** Lien « mon mandat » : programme + financement + choix encodés en base64url(JSON) dans le hash (`#verdict?s=…`). */
import type { SharePayload } from "./state.js";

/**
 * Code opaque d'un identifiant de mesure ou de variante dans le lien v3 (red team v0.2 m3 : un identifiant comme le nom
 * d'un auteur de proposition ne doit pas apparaître dans la charge base64). FNV-1a 32 bits en base 36 : déterministe,
 * résolu côté lecteur en hachant le catalogue chargé. Ce n'est pas un secret, seulement un identifiant non lisible.
 */
export function idCode(id: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < id.length; i++) {
    h ^= id.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(36);
}

/** Table code → identifiant ; null si deux identifiants partagent un code (le lien retombe alors sur les identifiants en clair). */
export function codeTable(ids: readonly string[]): Map<string, string> | null {
  const t = new Map<string, string>();
  for (const id of ids) {
    const c = idCode(id);
    const prev = t.get(c);
    if (prev !== undefined && prev !== id) return null;
    t.set(c, id);
  }
  return t;
}

function toBase64Url(s: string): string {
  const bytes = new TextEncoder().encode(s);
  let bin = "";
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function fromBase64Url(s: string): string {
  const b64 = s.replace(/-/g, "+").replace(/_/g, "/") + "=".repeat((4 - (s.length % 4)) % 4);
  const bin = atob(b64);
  const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

export function encodeShare(payload: SharePayload): string {
  return toBase64Url(JSON.stringify(payload));
}

export function decodeShare(token: string): SharePayload | null {
  try {
    const obj = JSON.parse(fromBase64Url(token)) as unknown;
    if (!obj || typeof obj !== "object" || !Array.isArray((obj as SharePayload).p)) return null;
    return obj as SharePayload;
  } catch {
    return null;
  }
}

/** URL absolue du mandat courant, pointant sur l'écran demandé. */
export function shareUrl(screen: string, payload: SharePayload): string {
  const base = `${location.origin}${location.pathname}${location.search}`;
  return `${base}#${screen}?s=${encodeShare(payload)}`;
}

/** Lit `#screen?s=…` ; renvoie l'écran et la charge éventuelle. */
export function parseHash(hash: string): { screen: string; share: SharePayload | null } {
  const h = hash.replace(/^#/, "");
  const [screen = "", query = ""] = h.split("?");
  const params = new URLSearchParams(query);
  const token = params.get("s");
  return { screen, share: token ? decodeShare(token) : null };
}

export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}
